import { useGetFormSubmissionsWithFieldsQuery } from "@/services/FormBuilderApi";
import { DataTable } from "@/components/ui/table/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Card } from "@/components/ui/Card";
import { DataTableColumnHeader } from "@/components/ui/table/DataTableColumnHeader";
import useTableData from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import Pagination from "@/components/ui/table/PaginationTable";
import usePagination from "@/hooks/UsePagination";
import { useState, useMemo, useCallback } from "react";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import { displayValue, downloadCSV } from "@/utils/Helper";
import { Checkbox } from "@/components/ui/CheckBox";
import { Table } from "@tanstack/react-table";
import { DataTableViewOptions } from "@/components/admin/tickets/component/DataTableViewOption";
import { useToast } from "@/components/ui/UseToast";

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};

export const FormSubmissionList = () => {
  useDocumentTitle("Form Submissions");
  const [inputValue, setInputValue] = useState<string>("");
  const [searchParams] = useSearchParams();
  const form_id = searchParams.get("id");
  const { toast } = useToast();
  const { searchCriteria, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);
  const [tableInstance, setTableInstance] = useState<Table<any> | null>(null);

  const { data: response, isLoading } = useGetFormSubmissionsWithFieldsQuery({
    form_id: form_id!,
    offset: searchCriteria.offset,
    limit: searchCriteria.limit,
    search_key: inputValue || undefined,
    sort_key: searchCriteria.sort_key,
    sort_order: searchCriteria.sort_order,
  });

  const submissions =
    response && "rows" in response ? response.rows : [];

  const totalRecords =
    response && "filtered_count" in response ? response.filtered_count : 0;

  const handleExportSelected = useCallback(() => {
    if (!tableInstance) {
      console.error("Table instance is not initialized");
      return;
    }

    const selectedRows = tableInstance
      .getSelectedRowModel()
      .rows.map((row) => row.original);

    if (selectedRows.length === 0) {
      toast({
        variant: "destructive",
        title: "Please select one or more records to perform this action.",
      });
      return;
    }

    const mapper = (row: any) => {
      const data: any = {};

      // Dynamic columns
      if (response && "columns" in response) {
        response.columns.forEach((col: any) => {
          let value = row.values?.[col.field_id];
          if (value === null || value === undefined) {
            value = "";
          } else if (col.type === "checkbox" || col.type === "checkbox-group") {
            if (Array.isArray(value)) value = value.join(", ");
          } else if (col.type === "date" || col.type === "datetime") {
            try {
              value = format(new Date(value), "yyyy-MM-dd HH:mm");
            } catch (e) {
              // keep as is
            }
          }
          data[col.label] = value;
        });
      }

      data["Submitted By"] = row.submitted_by || row.created_by || "";
      data["Submitted At"] = row.created_at ? format(new Date(row.created_at), "yyyy-MM-dd HH:mm") : "";

      return data;
    };

    downloadCSV(selectedRows, `Form_Submissions_${response && "form_name" in response ? (response as any).form_name : "Export"}.csv`, mapper);
  }, [tableInstance, response, toast]);

  const dynamicColumns = useMemo(() => {
    if (!response || !("columns" in response)) return [];

    return response.columns.map((col: any) => ({
      accessorKey: `values.${col.field_id}`,
      header: ({ column }: any) => (
        <DataTableColumnHeader
          column={column}
          title={col.label}
          sortKey={`values.${col.field_id}`}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }: any) => {
        const value = row.original.values?.[col.field_id];

        if (value === null || value === undefined) {
          return "-";
        }

        switch (col.type) {
          case "file":
            if (typeof value === "string" && value.startsWith("http")) {
              return (
                <a href={value} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                  View File
                </a>
              );
            }
            return value;
          case "checkbox":
          case "checkbox-group":
            if (Array.isArray(value)) {
              return value.join(", ");
            }
            return value;
          case "date":
          case "datetime":
            try {
              return format(new Date(value), "yyyy-MM-dd HH:mm");
            } catch (e) {
              return value;
            }
          default:
            return (
              <div className="flex items-center text-ellipsis whitespace-nowrap overflow-hidden max-w-[200px]" title={String(value)}>
                {String(value)}
              </div>
            );
        }
      },
    }));
  }, [response]);

  const staticColumns: ColumnDef<any>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "submitted_by",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Submitted By"
          sortKey="submitted_by"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => (
        <div className="flex items-center text-ellipsis whitespace-nowrap overflow-hidden max-w-[200px]" title={row.original.submitted_by || row.original.created_by || "-"}>
          {row.original.submitted_by || row.original.created_by || "-"}
        </div>
      ),
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Submitted At"
          sortKey="created_at"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const date = row.getValue("created_at") as string;
        return date ? format(new Date(date), "yyyy-MM-dd HH:mm") : "-";
      },
    },
    ...dynamicColumns,
  ];

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <div className="w-full p-5">
      <Card className="pt-4">
        <div className="flex flex-row lg:items-center justify-between gap-4 px-3">
          <TableSearchFilter
            inputValue={inputValue}
            setInputValue={setInputValue}
            setSearchCriteria={setSearchCriteria}
            placeHolder={"Search Submissions"}
          />
          <DataTableViewOptions action={handleExportSelected} />
        </div>

        <div className="mt-4 bg-white">
          <DataTable
            columns={staticColumns}
            data={submissions}
            isLoading={isLoading}
            searchMessage="No submissions found"
            onTableInit={setTableInstance}
          />
        </div>

        {submissions.length > 0 && (
          <Pagination
            limit={searchCriteria.limit}
            offset={searchCriteria.offset}
            totalItems={totalRecords}
            onLimitChange={handleLimitChange}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
            onFirstPage={handleFirstPage}
            onLastPage={handleLastPage}
            isLastPage={isLastPage}
          />
        )}
      </Card>
    </div>
  );
};
